/**
 * ActiveRideCard — driver active-ride UI (iter-70 decomposition slice #1).
 *
 * Extracted verbatim from Driver.jsx lines 1152–1547 to break up the
 * 1700-line monolith. NOTHING about the UX, business behaviour, or
 * data flow changes — this is a pure JSX move:
 *
 *   • All derived state (phase, isPickupLeg, isAtPickup, isInTransit,
 *     canConfirmArrival, canEndTrip, distanceToPickupKm,
 *     distanceToDropoffKm, gpsStatus, tripView, navApp) remains
 *     computed in the parent and arrives as props. The component does
 *     ZERO derivation of its own so prop snapshots are 1:1 reproductions
 *     of the previous inline render.
 *   • All side-effect callers (confirmArrival, startTrip, completeTrip,
 *     setTripViewPersisted, setChatOpen, setDirectionsRoute,
 *     setCurrentLoc) are passed in as props. The parent still owns
 *     state — this component is fully presentational.
 *   • All data-testid attributes are preserved EXACTLY so regression
 *     tests + Playwright selectors continue to work without changes.
 *
 * Rollback safety: deleting this file and pasting the original JSX
 * block back into Driver.jsx between `{activeRide && (` and the
 * matching `)}` restores byte-equivalent behaviour. No coupling to
 * any other moved code.
 *
 * Out of scope (per iter-70 spec):
 *   - No new derived booleans
 *   - No new dispatch / lifecycle / state-machine logic
 *   - No new offer-routing behaviour
 *   - No new messaging behaviour
 *   - GPS simulator's `api.post("/driver/location", ...)` call is
 *     left exactly as-is — moving it would require also moving the
 *     api import and is a separate decision.
 */
import React from "react";
import {
  Phone,
  MessageSquare,
  MapPin,
  Navigation,
  Flag,
  Check,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { launchNavigation } from "../../lib/navLaunch";
import { Button } from "../ui/button";
import { DirectionsPanel } from "../DirectionsPanel";
import { FareMeterPanel } from "../FareMeterPanel";
import { LiveFareBar } from "./LiveFareBar";
import { MiniStat } from "./MiniStat";
import { PhoneBookingSmsButton } from "./PhoneBookingSmsButton";


// ---------------------------------------------------------------------------
// GpsHealthStrip — the green/amber/red device-GPS indicator. Extracted as a
// local helper because it has three inline-returned branches that would
// otherwise dominate the parent JSX. Pure render, no state.
// ---------------------------------------------------------------------------
function GpsHealthStrip({ gpsStatus, isPickupLeg }) {
  const ageMs = gpsStatus.lastFixAt ? Date.now() - gpsStatus.lastFixAt : null;
  const isStale = gpsStatus.available === true && ageMs !== null && ageMs > 30000;
  const isBlocked = gpsStatus.available === false;
  const isLive = gpsStatus.available === true && !isStale;

  if (isLive) {
    return (
      <div className="border border-[#10B981] bg-[#ECFDF5] px-3 py-2 flex items-center gap-2 text-xs" data-testid="gps-status-live">
        <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-active" />
        <span className="font-bold text-[#065F46]">LIVE GPS</span>
        <span className="text-[#065F46]">· ±{gpsStatus.accuracy ?? "?"}m</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-[#10B981] opacity-70">auto-arrival armed</span>
      </div>
    );
  }
  if (isStale) {
    return (
      <div className="border-2 border-[#FFC107] bg-[#FFFBEB] px-3 py-2 text-xs" data-testid="gps-status-stale">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
          <span className="font-bold text-[#92400E]">GPS STALE · last fix {Math.round(ageMs / 1000)}s ago</span>
        </div>
        <div className="text-[#92400E] mt-1">
          If auto-arrival doesn't trigger, use the manual confirm below.
        </div>
      </div>
    );
  }
  if (isBlocked) {
    return (
      <div className="border-2 border-[#EF4444] bg-[#FEF2F2] px-3 py-2 text-xs" data-testid="gps-status-blocked">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#EF4444]" />
          <span className="font-bold text-[#991B1B]">GPS UNAVAILABLE</span>
          <span className="text-[#991B1B]">· {gpsStatus.code === "permission_denied" ? "permission denied" : gpsStatus.code === "unsupported" ? "not supported" : "no signal"}</span>
        </div>
        <div className="text-[#991B1B] mt-1">
          Auto-arrival is off. Use the manual confirm button below when you reach the {isPickupLeg ? "pickup" : "drop-off"}.
        </div>
      </div>
    );
  }
  return (
    <div className="border border-border bg-secondary/30 px-3 py-2 flex items-center gap-2 text-xs" data-testid="gps-status-pending">
      <span className="w-2 h-2 rounded-full bg-muted-foreground animate-pulse" />
      <span className="font-bold text-muted-foreground">ACQUIRING GPS…</span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PhaseCtaTree — the primary action block (Navigate + Confirm Arrival /
// Start Trip / Navigate + End Trip). Logic is BYTE-IDENTICAL to the
// previous inline IIFE; we just give it a named function so the diff is
// reviewable. Pure render.
// ---------------------------------------------------------------------------
function PhaseCtaTree({
  activeRide,
  isPickupLeg, isAtPickup,
  canConfirmArrival, canEndTrip,
  navApp,
  confirmArrival, startTrip, completeTrip, advanceStop,
}) {
  // Pickup leg destination = pickup; otherwise dropoff.
  const aimingAtPickup = isPickupLeg || isAtPickup;
  const destLat = aimingAtPickup ? activeRide.pickup_lat : activeRide.dropoff_lat;
  const destLng = aimingAtPickup ? activeRide.pickup_lng : activeRide.dropoff_lng;
  const navLabel = aimingAtPickup ? "PICKUP" : "DROP-OFF";

  // === Phase 1: en route to pickup (assigned / driver_en_route) ===
  if (isPickupLeg) {
    return (
      <div className="space-y-2" data-testid="phase-navigate-pickup">
        <button
          type="button"
          className={`flex items-center justify-center gap-2 w-full h-14 px-4 text-base font-bold tracking-wide transition-colors ${
            navApp === "waze"
              ? "bg-[#33CCFF] hover:bg-[#1FB8E8] text-black"
              : "bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
          }`}
          data-testid="navigate-btn"
          onClick={() => {
            toast.success(`Opening ${navApp === "waze" ? "Waze" : "Google Maps"} → ${navLabel}`);
            launchNavigation({ lat: destLat, lng: destLng, app: navApp });
          }}
        >
          <Navigation className="w-5 h-5" />
          NAVIGATE TO {navLabel} · {navApp === "waze" ? "WAZE" : "GOOGLE MAPS"}
        </button>
        <Button
          onClick={() => confirmArrival()}
          disabled={!canConfirmArrival}
          className="w-full h-14 text-base font-bold tracking-wide bg-[#10B981] hover:bg-[#059669] disabled:bg-secondary disabled:text-muted-foreground disabled:cursor-not-allowed"
          data-testid="confirm-arrival-btn"
        >
          <MapPin className="w-5 h-5 mr-2" />
          CONFIRM ARRIVAL
        </Button>
        {!canConfirmArrival && (
          <button
            type="button"
            onClick={() => confirmArrival({ manual: true })}
            className="w-full h-12 text-sm font-bold tracking-wide border-2 border-[#FFC107] bg-[#FFFBEB] hover:bg-[#FEF3C7] text-[#92400E] flex items-center justify-center gap-2 transition-colors uppercase"
            data-testid="manual-arrived-pickup-btn"
          >
            <MapPin className="w-4 h-4" />
            I'm at pickup — confirm manually
          </button>
        )}
        <p className="text-xs text-muted-foreground text-center">
          Auto-arrival fires within 100m of pickup (live GPS).
        </p>
      </div>
    );
  }

  // === Phase 2: at pickup, waiting for passenger to board ===
  if (isAtPickup) {
    // For a multi-stop journey the upcoming leg is the FIRST stop, not the
    // final drop-off — surface that so the driver knows where they head next.
    const stops = activeRide.stops || [];
    const nextLabel = stops.length > 0 ? `NEXT · STOP 1 OF ${stops.length}` : "NEXT · DROP-OFF";
    const nextAddr = stops.length > 0 ? stops[0].address : activeRide.dropoff_address;
    return (
      <div className="space-y-2" data-testid="phase-start-trip">
        {/* Surface the upcoming destination address immediately so the
            driver doesn't feel they've hit a dead-end state after
            CONFIRM ARRIVAL. The DirectionsPanel above also shows the
            full preview route. */}
        {nextAddr && (
          <div className="border-2 border-dashed border-[#FFC107] bg-[#FFFBEB] px-3 py-2 text-xs" data-testid="next-leg-banner">
            <div className="label-mini text-[#92400E] mb-0.5">{nextLabel}</div>
            <div className="font-semibold text-foreground truncate">{nextAddr}</div>
          </div>
        )}
        <Button
          onClick={startTrip}
          className="w-full h-14 text-base font-bold tracking-wide bg-[#10B981] hover:bg-[#059669]"
          data-testid="start-trip-btn"
        >
          <Flag className="w-5 h-5 mr-2" /> START TRIP
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Tap once {activeRide.passenger_name} is in the vehicle.
        </p>
        <button
          type="button"
          className="flex items-center justify-center gap-2 w-full h-10 px-4 border-2 border-border hover:bg-secondary text-xs font-semibold text-[#0A0A0A] transition-colors"
          data-testid="navigate-btn-secondary"
          onClick={() => launchNavigation({ lat: destLat, lng: destLng, app: navApp })}
        >
          <Navigation className="w-3.5 h-3.5" />
          Re-open navigation
        </button>
      </div>
    );
  }

  // === Phase 3: trip in progress ===
  // Multi-stop journeys (#8) progress pickup → stop₁ → … → stopₙ → drop-off.
  // `current_stop_index` counts stops already visited; while stops remain
  // the driver navigates to the NEXT stop and taps "Arrived at stop" to
  // advance. Only once all stops are done do the drop-off nav + END TRIP
  // appear. This is additive UI/sequencing — the ride stays in_progress and
  // completion is still geofenced to the final drop-off on the backend.
  const stops = activeRide.stops || [];
  const stopIdx = activeRide.current_stop_index ?? 0;
  const hasPendingStop = stops.length > 0 && stopIdx < stops.length;

  if (hasPendingStop) {
    const stop = stops[stopIdx];
    const isLastStop = stopIdx === stops.length - 1;
    const remaining = stops.length - stopIdx - 1;
    return (
      <div className="space-y-2" data-testid="phase-navigate-stop">
        <div className="border-2 border-dashed border-[#002FA7] bg-[#EFF3FF] px-3 py-2 text-xs" data-testid="current-stop-banner">
          <div className="label-mini text-[#002FA7] mb-0.5">NEXT · STOP {stopIdx + 1} OF {stops.length}</div>
          <div className="font-semibold text-foreground truncate" data-testid="current-stop-address">{stop.address}</div>
        </div>
        <button
          type="button"
          className={`flex items-center justify-center gap-2 w-full h-14 px-4 text-base font-bold tracking-wide transition-colors ${
            navApp === "waze"
              ? "bg-[#33CCFF] hover:bg-[#1FB8E8] text-black"
              : "bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
          }`}
          data-testid="navigate-btn"
          onClick={() => {
            toast.success(`Opening ${navApp === "waze" ? "Waze" : "Google Maps"} → STOP ${stopIdx + 1}`);
            launchNavigation({ lat: stop.lat, lng: stop.lng, app: navApp });
          }}
        >
          <Navigation className="w-5 h-5" />
          NAVIGATE TO STOP {stopIdx + 1} · {navApp === "waze" ? "WAZE" : "GOOGLE MAPS"}
        </button>
        <Button
          onClick={() => advanceStop()}
          className="w-full h-14 text-base font-bold tracking-wide bg-[#002FA7] hover:bg-[#001f6e]"
          data-testid="arrived-at-stop-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          ARRIVED AT STOP {stopIdx + 1} — {isLastStop ? "CONTINUE TO DROP-OFF" : "CONTINUE TO NEXT STOP"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          {isLastStop
            ? "After this stop you'll head to the final drop-off."
            : `${remaining} more stop${remaining > 1 ? "s" : ""} after this, then the drop-off.`}
        </p>
      </div>
    );
  }

  // All stops visited (or none) → final drop-off leg.
  return (
    <div className="space-y-2" data-testid="phase-navigate-dropoff">
      <button
        type="button"
        className={`flex items-center justify-center gap-2 w-full h-14 px-4 text-base font-bold tracking-wide transition-colors ${
          navApp === "waze"
            ? "bg-[#33CCFF] hover:bg-[#1FB8E8] text-black"
            : "bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
        }`}
        data-testid="navigate-btn"
        onClick={() => {
          toast.success(`Opening ${navApp === "waze" ? "Waze" : "Google Maps"} → DROP-OFF`);
          launchNavigation({ lat: destLat, lng: destLng, app: navApp });
        }}
      >
        <Navigation className="w-5 h-5" />
        NAVIGATE TO DROP-OFF · {navApp === "waze" ? "WAZE" : "GOOGLE MAPS"}
      </button>
      <Button
        onClick={() => completeTrip()}
        disabled={!canEndTrip}
        className="w-full h-14 text-base font-bold tracking-wide bg-[#10B981] hover:bg-[#059669] disabled:bg-secondary disabled:text-muted-foreground disabled:cursor-not-allowed"
        data-testid="complete-trip-btn"
      >
        <Check className="w-5 h-5 mr-2" />
        END TRIP &amp; COLLECT FARE
      </Button>
      {!canEndTrip && (
        <button
          type="button"
          onClick={() => completeTrip({ manual: true })}
          className="w-full h-12 text-sm font-bold tracking-wide border-2 border-[#FFC107] bg-[#FFFBEB] hover:bg-[#FEF3C7] text-[#92400E] flex items-center justify-center gap-2 transition-colors uppercase"
          data-testid="manual-arrived-dropoff-btn"
        >
          <Check className="w-4 h-4" />
          I'm at drop-off — end trip manually
        </button>
      )}
      <p className="text-xs text-muted-foreground text-center">
        Auto-arrival fires within 100m of drop-off (live GPS).
      </p>
    </div>
  );
}


// ===========================================================================
// ActiveRideCard — public component.
// ===========================================================================
export function ActiveRideCard({
  activeRide,
  // Phase booleans (derived in parent)
  phase, isPickupLeg, isAtPickup, isInTransit,
  // Geofence flags
  canConfirmArrival, canEndTrip,
  // Live distances
  distanceToPickupKm, distanceToDropoffKm,
  // Online status (gates MESSAGE button)
  isOnline,
  // Real-device GPS status
  gpsStatus,
  // Nav/Meter toggle state
  tripView, setTripViewPersisted,
  // Nav app preference (google | waze)
  navApp,
  // Setters owned by the parent
  setChatOpen,
  incomingMessage,
  setDirectionsRoute,
  setNavInfo,
  setCurrentLoc,
  directionsRefreshSignal = 0,
  // Lifecycle handlers (parent owns the API calls)
  confirmArrival, startTrip, completeTrip, advanceStop,
}) {
  // Slice 2 — masked voice call. The driver dials the SHARED proxy number
  // (never the customer's real number); the Twilio voice webhook identifies
  // the driver by their on-file caller ID and bridges to the customer.
  const callCustomer = async () => {
    try {
      const res = await api.get(`/driver/rides/${activeRide.id}/call`);
      const data = res?.data || {};
      if (data.available && data.call_number) {
        toast.success("Connecting through Crystal Dispatch…", { description: data.note });
        window.location.href = `tel:${data.call_number}`;
      } else {
        toast.message(data.reason || "Calling isn't available right now.");
      }
    } catch (e) {
      toast.error("Couldn't start the call. Please try again.");
    }
  };
  return (
    <div className="border-4 border-[#002FA7] bg-white p-5 space-y-4 shadow-[0_0_0_4px_rgba(0,47,167,0.15)]" data-testid="active-ride-card">
      <div>
        <div className="label-mini text-[#002FA7] flex items-center gap-2" data-testid="active-ride-phase-label">
          <span
            className={`w-2.5 h-2.5 rounded-full ${
              isAtPickup || canEndTrip ? "bg-[#10B981]" : "bg-[#002FA7]"
            } pulse-warning`}
          />
          {isPickupLeg && (canConfirmArrival ? "AT PICKUP — CONFIRM ARRIVAL" : "EN ROUTE TO PICKUP")}
          {isAtPickup && "ARRIVED AT PICKUP — START TRIP"}
          {isInTransit && (canEndTrip ? "AT DROP-OFF — COLLECT FARE" : "IN TRANSIT TO DROP-OFF")}
        </div>
        <h3 className="font-heading text-2xl font-black tracking-tight mt-1">{activeRide.passenger_name}</h3>

        {/* Contact row. CALL bridges driver↔customer through Twilio voice
            masking (Slice 2): the driver dials a shared proxy number and the
            webhook connects the customer — the customer's real number is NEVER
            shown to the driver (privacy contract). For phone bookings
            (booked_by_dispatch) the customer has no app, so in-app MESSAGE is
            replaced with the SMS bridge button. */}
        {(() => {
          const isPhoneBooking = activeRide.booked_by_dispatch === true;
          return (
            <>
              <div className="flex gap-2 mt-2" data-testid="ride-contact-row">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={callCustomer}
                  className="text-xs h-8 flex-1"
                  data-testid="ride-call-btn"
                >
                  <Phone className="w-3 h-3 mr-1.5" /> CALL
                </Button>
                {isPhoneBooking ? (
                  <PhoneBookingSmsButton rideId={activeRide.id} incomingMessage={incomingMessage} />
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={!isOnline}
                    onClick={() => {
                      // Backend rejects offline-driver sends with 403; mirror
                      // that here so the dialog never opens with chips that
                      // would just fail, and nudge the driver to go online.
                      if (!isOnline) {
                        toast.message("Go online to message passenger.", {
                          description: "Flip the GO ONLINE switch first.",
                        });
                        return;
                      }
                      setChatOpen(true);
                    }}
                    className="text-xs h-8 flex-1 bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 disabled:opacity-60"
                    data-testid="ride-message-btn"
                  >
                    <MessageSquare className="w-3 h-3 mr-1.5" />
                    {isOnline ? "MESSAGE" : "GO ONLINE TO MESSAGE"}
                  </Button>
                )}
              </div>
              {isPhoneBooking && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="ride-phone-booking-help">
                  Texts the customer as SMS from Crystal Dispatch. Their number stays private.
                </p>
              )}
            </>
          );
        })()}

        {/* Live distance read-out — surfaces the geofence visually so
            the driver can see auto-arrival approaching. */}
        <div className="text-xs text-muted-foreground font-mono-tab mt-1" data-testid="active-ride-distance">
          {(isPickupLeg || isAtPickup)
            ? `${distanceToPickupKm !== null && isFinite(distanceToPickupKm) ? (distanceToPickupKm * 1000).toFixed(0) : "—"} m to pickup`
            : `${distanceToDropoffKm !== null && isFinite(distanceToDropoffKm) ? (distanceToDropoffKm * 1000).toFixed(0) : "—"} m to drop-off`}
          {(canConfirmArrival || canEndTrip) && (
            <span className="ml-2 text-[#10B981] font-bold">· within 100m</span>
          )}
          <span className="ml-2 text-[10px] uppercase tracking-wider opacity-60" data-testid="active-ride-phase-tag">                    · phase {phase}
          </span>
        </div>
      </div>

      {/* NAV ⇄ METER toggle — rendered NEAR THE TOP of the active-ride
          card during trip_in_progress so it's always reachable on
          mobile (previously it was buried below the CTA tree and fell
          off-screen). The CTA tree (END TRIP) below is unaffected by
          the switch so Complete Trip stays visible in BOTH views.
          View choice is persisted in sessionStorage so a reload (or
          bfcache restore) doesn't kick the driver back to NAV mid-trip. */}
      {isInTransit && (
        <div className="flex" data-testid="trip-view-toggle">
          <button
            type="button"
            onClick={() => setTripViewPersisted("nav")}
            className={`flex-1 h-10 text-xs font-bold uppercase tracking-wider border transition-colors ${
              tripView === "nav"
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-foreground border-border hover:bg-secondary"
            }`}
            data-testid="trip-view-nav-btn"
            aria-pressed={tripView === "nav"}
          >
            Navigation
          </button>
          <button
            type="button"
            onClick={() => setTripViewPersisted("meter")}
            className={`flex-1 h-10 text-xs font-bold uppercase tracking-wider border-y border-r transition-colors ${
              tripView === "meter"
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-foreground border-border hover:bg-secondary"
            }`}
            data-testid="trip-view-meter-btn"
            aria-pressed={tripView === "meter"}
          >
            Estimated live fare
          </button>
        </div>
      )}

      {/* PERSISTENT live-fare strip — always visible on the Navigation tab so
          the driver sees the running fare the instant they're back in Dispatch
          OS from the external Google Maps app, without switching tabs. One tap
          jumps to the full FareMeterPanel breakdown (METER tab). On the METER
          tab the full meter already shows the fare, so the strip is redundant
          there and hidden. */}
      {isInTransit && tripView === "nav" && (
        <LiveFareBar
          rideId={activeRide.id}
          fallbackEstimate={activeRide.estimated_fare}
          onExpand={() => setTripViewPersisted("meter")}
        />
      )}

      {/* GPS health strip — surfaces real device-GPS state during an
          active ride so the driver knows whether auto-arrival can be
          trusted. Three states:
            · live (green)    — fresh fix within last 30s
            · stale (amber)   — fix older than 30s
            · blocked (red)   — permission denied / unsupported
          When blocked, we explicitly point the driver at the manual
          confirm button so they never hit a dead-end. */}
      {(isPickupLeg || isInTransit) && (
        <GpsHealthStrip gpsStatus={gpsStatus} isPickupLeg={isPickupLeg} />
      )}

      {/* PRIMARY ACTION — PHASE-FIRST CTA TREE.
          The backend phase drives the visible button; local
          proximity only ENABLES the explicit confirm action so the
          driver can't accidentally tap from miles away.
          - assigned / driver_en_route → Navigate (+ Confirm Arrival when within 100m)
          - driver_arrived_pickup     → START TRIP
          - trip_in_progress          → Navigate (+ End Trip when within 100m) */}
      <PhaseCtaTree
        activeRide={activeRide}
        isPickupLeg={isPickupLeg}
        isAtPickup={isAtPickup}
        canConfirmArrival={canConfirmArrival}
        canEndTrip={canEndTrip}
        navApp={navApp}
        confirmArrival={confirmArrival}
        startTrip={startTrip}
        completeTrip={completeTrip}
        advanceStop={advanceStop}
      />

      {/* Turn-by-turn directions for the active leg — rendered
          AFTER the CTA so the action stays above the fold.
          During trip_in_progress, this is gated by the NAV/METER
          toggle (driver chose Navigation). The driver_arrived_pickup
          phase always shows the preview route since there's no
          meter to run yet.
          - assigned / driver_en_route  → live nav to pickup
          - driver_arrived_pickup       → PREVIEW of the upcoming
            dropoff leg (so the driver sees where they're heading
            while the passenger boards)
          - trip_in_progress + nav      → live nav to dropoff
          The component polls every 20s and shows a graceful
          amber fallback if the backend can't reach Google. */}
      {(isPickupLeg || isAtPickup || (isInTransit && tripView === "nav")) && (
        <DirectionsPanel
          rideId={activeRide.id}
          phase={phase}
          stopIndex={activeRide.current_stop_index ?? 0}
          onRoute={setDirectionsRoute}
          onNav={setNavInfo}
          previewDropoff={isAtPickup}
          refreshSignal={directionsRefreshSignal}
        />
      )}

      {/* Estimated live fare meter (driver-only this iteration).
          Polls /driver/live-fare every 5s; client-side 1s elapsed
          ticker. Active ride state is owned by the parent — this
          panel reads from it, never mutates. */}
      {isInTransit && tripView === "meter" && (
        <FareMeterPanel
          rideId={activeRide.id}
          startedAt={activeRide.started_at}
          fallbackEstimate={activeRide.estimated_fare}
        />
      )}

      <div className="text-sm space-y-1">
        <div className="flex items-start gap-2">
          <span className="w-2 h-2 rounded-full bg-[#10B981] mt-1.5 flex-shrink-0" />
          <span className="font-semibold">{activeRide.pickup_address}</span>
        </div>
        {(activeRide.stops || []).map((st, i, arr) => {
          const stopIdx = activeRide.current_stop_index ?? 0;
          const isNext = isInTransit && i === stopIdx;
          const isVisited = isInTransit && i < stopIdx;
          return (
            <div key={i} className="flex items-start gap-2" data-testid={`active-ride-stop-${i}`}>
              <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isVisited ? "bg-muted-foreground" : "bg-[#002FA7]"}`} />
              <span className={`font-semibold ${isVisited ? "line-through text-muted-foreground" : ""}`}>
                {st.address}
                <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">stop {i + 1} of {arr.length}</span>
                {isNext && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider font-bold bg-[#002FA7] text-white px-1.5 py-0.5" data-testid={`active-ride-stop-next-${i}`}>Next</span>
                )}
                {isVisited && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider font-bold text-[#10B981]" data-testid={`active-ride-stop-done-${i}`}>✓ Done</span>
                )}
              </span>
            </div>
          );
        })}
        <div className="flex items-start gap-2">
          <span className="w-2 h-2 rounded-full bg-[#EF4444] mt-1.5 flex-shrink-0" />
          <span className="font-semibold">{activeRide.dropoff_address}</span>
        </div>
      </div>

      {activeRide.driver_notes && (
        <div
          data-testid="active-ride-driver-notes"
          className="border-l-2 border-[#FFC107] bg-[#FFFBEB] px-3 py-2 text-xs"
        >
          <div className="label-mini text-[#92400E] mb-0.5">NOTES FROM PASSENGER</div>
          <div className="font-semibold text-[#1F2937] whitespace-pre-line break-words">
            {activeRide.driver_notes}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <MiniStat icon={<Navigation className="w-3 h-3" />} label="DIST" value={`${activeRide.distance_mi ?? activeRide.distance_km} mi`} />
        <MiniStat icon={<DollarSign className="w-3 h-3" />} label="EST. FARE" value={`£${activeRide.estimated_fare}`} />
      </div>

      {/* GPS simulator — handy in the demo environment (no real device
          GPS) and gives drivers a way to "snap to" the pickup/drop-off
          for testing the 4-phase auto-arrival behaviour. */}
      <div className="border-t border-dashed border-border pt-3" data-testid="gps-simulator">
        <div className="label-mini text-muted-foreground mb-2">SIMULATE GPS</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              const p = { name: "At Pickup", lat: activeRide.pickup_lat, lng: activeRide.pickup_lng };
              setCurrentLoc(p);
              api.post("/driver/location", { lat: p.lat, lng: p.lng });
            }}
            className="text-xs px-2 py-2 border border-border hover:bg-secondary font-semibold"
            data-testid="teleport-pickup-btn"
          >
            Teleport to pickup
          </button>
          <button
            type="button"
            onClick={() => {
              const p = { name: "At Drop-off", lat: activeRide.dropoff_lat, lng: activeRide.dropoff_lng };
              setCurrentLoc(p);
              api.post("/driver/location", { lat: p.lat, lng: p.lng });
            }}
            className="text-xs px-2 py-2 border border-border hover:bg-secondary font-semibold"
            data-testid="teleport-dropoff-btn"
          >
            Teleport to drop-off
          </button>
        </div>
      </div>
    </div>
  );
}
